import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isEqual } from 'lodash';

import { InvalidDataError } from '../api';
import Stages from './Stages';
import ReviewCampaign from './ReviewCampaign';
import AudienceEditor from './AudienceEditor';
import TemplateEditor from './TemplateEditor';

const Step = {
  STAGES: 'stages',
  REVIEW_CAMPAIGN: 'review-campaign',
  AUDIENCE_EDITOR: 'audience/editor',
  TEMPLATE_EDITOR: 'template/editor',
};

const COMPONENTS_BY_STEP = {
  [Step.STAGES]: Stages,
  [Step.REVIEW_CAMPAIGN]: ReviewCampaign,
  [Step.AUDIENCE_EDITOR]: AudienceEditor,
  [Step.TEMPLATE_EDITOR]: TemplateEditor,
};

const Context = createContext({
  getField() {
    throw new Error('Not implemented');
  },
  setField() {
    throw new Error('Not implemented');
  },
  goToStep() {
    throw new Error('Not implemented');
  },
  hasParam() {
    throw new Error('Not implemented');
  },
  getParam() {
    throw new Error('Not implemented');
  },
  getData() {
    throw new Error('Not implemented');
  },
  getAutosaveStatus() {
    throw new Error('Not implemented');
  },
  step: '',
  isPausedBannerDismissed: false,
  setPausedBannerDismissed() {
    throw new Error('Not implemented');
  },
});

const useSteps = initialStep => {
  // initialStep is always an object: { step: string, params?: object }
  // Default to stages if not provided
  const normalizedInitialStep = initialStep || {
    step: Step.STAGES,
    params: {},
  };

  const [activeStepContext, setActiveStepContext] = useState(
    normalizedInitialStep
  );

  // Update step when initialStep changes (e.g., when query params change)
  useEffect(() => {
    if (initialStep === null || initialStep === undefined) {
      return; // Don't update if initialStep is null/undefined
    }

    const newStepValue = initialStep.step;
    const newParams = initialStep.params || {};

    // Update if step changes OR if params changed (for same step)
    const stepChanged = newStepValue !== activeStepContext.step;
    const paramsChanged =
      JSON.stringify(newParams) !== JSON.stringify(activeStepContext.params);

    if (newStepValue && (stepChanged || paramsChanged)) {
      setActiveStepContext({
        step: newStepValue,
        params: newParams,
      });
    }
  }, [initialStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToStep = useCallback((step, params) => {
    setActiveStepContext({ step, params: params ?? {} });
  }, []);

  const hasParam = useCallback(
    name => {
      return Object.prototype.hasOwnProperty.call(
        activeStepContext.params,
        name
      );
    },
    [activeStepContext.params]
  );

  const getParam = useCallback(
    name => {
      if (!hasParam(name)) {
        throw new Error(`Param with name '${name}' is not provided`);
      }

      return activeStepContext.params[name];
    },
    [activeStepContext.params, hasParam]
  );

  return {
    step: activeStepContext.step,
    goToStep,
    hasParam,
    getParam,
  };
};

const CampaignEditor = ({
  initialData = {},
  confirmText,
  onConfirm,
  onTouch = () => {},
  initialStep,
  onDataChange,
  onBeforeStepChange,
  autosaveStatus,
  isPausedBannerDismissed: isPausedBannerDismissedProp,
  onPausedBannerDismissed,
}) => {
  const {
    step,
    goToStep: originalGoToStep,
    getParam,
    hasParam,
  } = useSteps(initialStep || { step: Step.STAGES, params: {} });
  const [data, setData] = useState(initialData);
  const [isPausedBannerDismissedInternal, setPausedBannerDismissedInternal] =
    useState(false);
  const isPausedBannerDismissed =
    isPausedBannerDismissedProp ?? isPausedBannerDismissedInternal;
  const setPausedBannerDismissed =
    onPausedBannerDismissed ?? setPausedBannerDismissedInternal;
  const lastCampaignIdRef = useRef(initialData?._id);
  const lastStagesLengthRef = useRef(initialData?.stages?.length);
  const lastAudienceRef = useRef(initialData?.audience);

  // Update data when initialData changes meaningfully:
  // - When data first loads (has _id but we haven't seen it)
  // - When a different campaign is loaded (_id changed)
  // - When stages structure changes (external modification)
  // - When audience changes (e.g., after applying from SavedAudiencesPage)
  useEffect(() => {
    const hasContent = initialData?._id;
    const isNewCampaign = lastCampaignIdRef.current !== initialData?._id;
    const stagesChanged =
      lastCampaignIdRef.current === initialData?._id &&
      lastStagesLengthRef.current !== initialData?.stages?.length;
    const audienceChanged =
      lastCampaignIdRef.current === initialData?._id &&
      !isEqual(initialData?.audience, lastAudienceRef.current);

    if (hasContent && (isNewCampaign || stagesChanged || audienceChanged)) {
      setData(initialData);
      lastCampaignIdRef.current = initialData._id;
      lastStagesLengthRef.current = initialData?.stages?.length;
      lastAudienceRef.current = initialData?.audience;
    }
  }, [initialData]);

  // Notify parent when data changes
  useEffect(() => {
    onDataChange?.(data);
  }, [data, onDataChange]);

  // Wrapped goToStep that saves before transitioning
  const goToStep = useCallback(
    async (newStep, params) => {
      // Only run validation if actually changing steps
      if (newStep !== step && onBeforeStepChange) {
        try {
          // Pass current editor data so validation can check the latest state
          await onBeforeStepChange(newStep, params, data);
        } catch (error) {
          console.error('Failed to save before step change:', error);
          // Only prevent navigation if it's an InvalidDataError (validation error)
          // Allow navigation for other errors (e.g., network errors during autosave)
          if (error instanceof InvalidDataError) {
            return; // Don't transition if validation fails
          }
          // For other errors, log but allow navigation to proceed
        }
      }
      originalGoToStep(newStep, params);
    },
    [onBeforeStepChange, originalGoToStep, step, data]
  );

  const editor = useMemo(
    () => ({
      goToStep,
      setField: (name, value) => {
        onTouch();
        setData(prevData => ({ ...prevData, [name]: value }));
      },
      getField: name => data[name],
      hasParam,
      getParam,
      getData: () => data,
      getAutosaveStatus: () => autosaveStatus,
      step,
      isPausedBannerDismissed,
      setPausedBannerDismissed,
    }),
    [
      data,
      getParam,
      goToStep,
      hasParam,
      onTouch,
      autosaveStatus,
      step,
      isPausedBannerDismissed,
    ]
  );

  const StepComponent = COMPONENTS_BY_STEP[step];
  const PROPS_BY_STEP = {
    [Step.REVIEW_CAMPAIGN]: { confirmText, onConfirm },
  };
  return (
    <Context.Provider value={editor}>
      <StepComponent {...PROPS_BY_STEP[step]} autosaveStatus={autosaveStatus} />
    </Context.Provider>
  );
};

export const useEditor = () => {
  return useContext(Context);
};

export default CampaignEditor;
